import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import {
    canvasToBlob,
    copyText,
    loadImage,
    shareImage,
    ShareImageResult,
    wrapLines,
} from '@gagovictor/shared-frontend-core';

export interface ShareCardInput {
    cardId: string;
    name: string;
    reversed: boolean;
    /** Heading above the advice, e.g. "Conselho de hoje". */
    adviceLabel: string;
    advice: string;
    /** Shown under the logo, e.g. "Arcana". */
    appName: string;
    /** Text that goes with the picture in the share sheet. */
    message: string;
}

/** Where Arcana lives on the web; printed on the picture and added to the share text. */
export const SITE_URL = 'https://blue-coast-0edc77010.1.azurestaticapps.net/';

const W = 1080;
const H = 1920;
const GOLD = '#d4af6a';

/** Draws the advice, shrinking the font until it fits `maxLines`. Returns the y below the block. */
const drawAdvice = (
    ctx: CanvasRenderingContext2D,
    text: string,
    y: number,
    maxLines: number
) => {
    let size = 52;
    let lines: string[] = [];
    for (; size >= 30; size -= 2) {
        ctx.font = `italic ${size}px "Cormorant Garamond", Georgia, serif`;
        lines = wrapLines((s) => ctx.measureText(s).width, text, W - 220);
        if (lines.length <= maxLines) break;
    }
    const lineHeight = size * 1.3;
    lines.forEach((l, i) => ctx.fillText(l, W / 2, y + i * lineHeight));
    return y + lines.length * lineHeight;
};

/** Renders the card of the day as a 9:16 picture, the shape stories and status updates use. */
export const renderShareCard = async (
    input: ShareCardInput
): Promise<HTMLCanvasElement> => {
    const base = process.env.PUBLIC_URL ?? '';
    const [card, logo] = await Promise.all([
        loadImage(`${base}/cards/${input.cardId}.webp`),
        loadImage(`${base}/logo192.png`).catch(() => null),
        document.fonts?.ready,
    ]);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available');

    const bg = ctx.createRadialGradient(W / 2, 700, 100, W / 2, 800, 1400);
    bg.addColorStop(0, '#3a1f66');
    bg.addColorStop(0.6, '#150a28');
    bg.addColorStop(1, '#07040e');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Logo and app name, top.
    if (logo) ctx.drawImage(logo, W / 2 - 40, 260, 80, 80);
    ctx.fillStyle = GOLD;
    ctx.font = '40px Cinzel, "Cormorant Garamond", Georgia, serif';
    ctx.fillText(input.appName.toUpperCase(), W / 2, 395);

    // The card, with a golden glow.
    const cw = 440;
    const ch = (cw * 340) / 200;
    const cx = (W - cw) / 2;
    const cy = 430;
    ctx.save();
    ctx.shadowColor = 'rgba(212,175,106,.55)';
    ctx.shadowBlur = 60;
    ctx.fillStyle = '#150e24';
    ctx.fillRect(cx, cy, cw, ch);
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx, cy, cw, ch);
    ctx.clip();
    if (input.reversed) {
        ctx.translate(cx + cw / 2, cy + ch / 2);
        ctx.rotate(Math.PI);
        ctx.drawImage(card, -cw / 2, -ch / 2, cw, ch);
    } else ctx.drawImage(card, cx, cy, cw, ch);
    ctx.restore();
    ctx.strokeStyle = 'rgba(212,175,106,.7)';
    ctx.lineWidth = 3;
    ctx.strokeRect(cx, cy, cw, ch);

    // Name.
    let y = cy + ch + 80;
    ctx.fillStyle = '#f5ecd8';
    ctx.font = '60px Cinzel, "Cormorant Garamond", Georgia, serif';
    ctx.fillText(input.name, W / 2, y);

    // Advice.
    y += 80;
    ctx.fillStyle = GOLD;
    ctx.font = '30px Cinzel, "Cormorant Garamond", Georgia, serif';
    ctx.fillText(input.adviceLabel.toUpperCase().split('').join(' '), W / 2, y);
    y += 70;
    ctx.fillStyle = '#f5ecd8';
    drawAdvice(ctx, input.advice, y, Math.max(2, Math.floor((1600 - y) / 60)));

    return canvas;
};

export type ShareResult = ShareImageResult;

/** Shares the card picture through the system share sheet, or downloads it where there is none. */
export const shareCard = async (input: ShareCardInput): Promise<ShareResult> =>
    shareImage({
        image: await canvasToBlob(await renderShareCard(input)),
        fileName: `arcana-${input.cardId}.png`,
        text: `${input.message} ${SITE_URL}`,
        share: Share,
        filesystem: Filesystem,
        cacheDirectory: Directory.Cache,
    });

/** Puts the site link on the clipboard, so it can be pasted as a Link sticker in Instagram Stories. */
export const copyLink = () => copyText(SITE_URL);
